import * as React from "react";
import Box from "@mui/material/Box";

/**
 * Segmented toggle — the design system's shared `.seg-toggle` / `.seg-pill`
 * (assets/neura.css), also used by ims/installers.html for its Active/Removed
 * split. Values are taken verbatim from that stylesheet.
 *
 * `options` is `[{ value, label, count }]`; `count` is optional and renders as
 * the pill's trailing badge.
 */
export default function SegToggle({ options, value, onChange, ariaLabel }) {
  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px",
        background: "rgba(255, 255, 255, 0.03)",
        border: "1px solid var(--border)",
        borderRadius: "10px",
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 12px",
              background: selected ? "var(--accent-tint)" : "transparent",
              border: "none",
              borderRadius: "8px",
              color: selected ? "var(--accent)" : "var(--text-secondary)",
              boxShadow: selected ? "inset 0 0 0 1px rgba(253, 185, 64, 0.3)" : "none",
              fontFamily: "inherit",
              fontSize: "0.78rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.18s var(--ease-out)",
              "&:hover": { color: selected ? "var(--accent)" : "var(--text)" },
            }}
          >
            {option.label}
            {option.count !== undefined && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.7rem",
                  fontVariantNumeric: "tabular-nums",
                  padding: "1px 6px",
                  borderRadius: "999px",
                  background: selected ? "rgba(253, 185, 64, 0.18)" : "rgba(255, 255, 255, 0.05)",
                  color: selected ? "var(--accent)" : "var(--text-muted)",
                }}
              >
                {option.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
